import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import stateList from '../../../components/common/states';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const validateWarehouse = (warehouse, idx) => {
  const errors = [];
  if (!warehouse.warehouse_name.trim()) errors.push(`Warehouse #${idx + 1}: Name is required`);
  else if (warehouse.warehouse_name.length > 120) errors.push(`Warehouse #${idx + 1}: Name cannot exceed 120 characters`);
  if (!warehouse.type) errors.push(`Warehouse #${idx + 1}: Type is required`);
  if (!warehouse.location.trim()) errors.push(`Warehouse #${idx + 1}: Location is required`);
  else if (warehouse.location.length > 120) errors.push(`Warehouse #${idx + 1}: Location cannot exceed 120 characters`);
  if (!warehouse.state.trim()) errors.push(`Warehouse #${idx + 1}: State is required`);
  else if (warehouse.state.length > 60) errors.push(`Warehouse #${idx + 1}: State cannot exceed 60 characters`);
  
  // Validate state_code - it should be auto-filled when state is selected
  if (!warehouse.state_code.trim()) errors.push(`Warehouse #${idx + 1}: State Code is required`);
  else if (warehouse.state_code.length > 10) errors.push(`Warehouse #${idx + 1}: State Code cannot exceed 10 characters`);
  
  return errors;
};

const validateForm = (form) => {
  const errors = {};
  // Warehouses
  let warehouseErrors = [];
  if (!form.warehouses || !Array.isArray(form.warehouses) || form.warehouses.length === 0) {
    warehouseErrors.push('At least one warehouse is required');
  } else {
    form.warehouses.forEach((wh, idx) => {
      warehouseErrors = warehouseErrors.concat(validateWarehouse(wh, idx));
    });
  }
  errors.warehouses = warehouseErrors;
  // Remove empty errors
  Object.keys(errors).forEach((key) => {
    if (!errors[key] || (Array.isArray(errors[key]) && errors[key].length === 0)) {
      delete errors[key];
    }
  });
  return errors;
};

const CmaAddWarehousesForm = () => {
  const { cmaId } = useParams();
  const navigate = useNavigate();
  const [cmaData, setCmaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    warehouses: [
      { warehouse_name: '', type: '', location: '', state: '', state_code: '' }
    ]
  });

  // Fetch CMA data on component mount
  useEffect(() => {
    const fetchCmaData = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/cma-get?page=1&limit=1000&search=`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to fetch CMA data');
        const result = await res.json();
        const cma = result.data.find(item => item.cha_cma_id === parseInt(cmaId));
        if (!cma) {
          toast.error('CMA not found');
          navigate('/cma');
          return;
        }
        setCmaData(cma);
      } catch (err) {
        toast.error('Failed to load CMA data');
        navigate('/cma');
      }
    };

    if (cmaId) {
      fetchCmaData();
    }
  }, [cmaId, navigate]);

  const handleWarehouseChange = (idx, e) => {
    if (e.target.name === 'state') {
      const selectedState = stateList.find(s => s.name === e.target.value);
      const newWarehouses = form.warehouses.map((w, i) =>
        i === idx ? { ...w, state: selectedState ? selectedState.name : '', state_code: selectedState ? selectedState.code : '' } : w
      );
      setForm({ ...form, warehouses: newWarehouses });
    } else {
      const newWarehouses = form.warehouses.map((w, i) =>
        i === idx ? { ...w, [e.target.name]: e.target.value } : w
      );
      setForm({ ...form, warehouses: newWarehouses });
    }
  };

  const handleWarehouseBlur = (idx, e) => {
    const { name, value } = e.target;
    let error = '';
    if (name === 'warehouse_name' && (!value.trim() || value.length > 120)) {
      error = !value.trim() ? `Warehouse #${idx + 1}: Name is required` : `Warehouse #${idx + 1}: Name cannot exceed 120 characters`;
    }
    if (name === 'type' && !value) {
      error = `Warehouse #${idx + 1}: Type is required`;
    }
    if (name === 'location' && (!value.trim() || value.length > 120)) {
      error = !value.trim() ? `Warehouse #${idx + 1}: Location is required` : `Warehouse #${idx + 1}: Location cannot exceed 120 characters`;
    }
    if (name === 'state' && (!value.trim() || value.length > 60)) {
      error = !value.trim() ? `Warehouse #${idx + 1}: State is required` : `Warehouse #${idx + 1}: State cannot exceed 60 characters`;
    }
    if (error) {
      toast.error(error);
    }
  };

  const handleAddWarehouse = () => {
    setForm({
      ...form,
      warehouses: [...form.warehouses, { warehouse_name: '', type: '', location: '', state: '', state_code: '' }]
    });
  };

  const handleRemoveWarehouse = (idx) => {
    setForm({
      ...form,
      warehouses: form.warehouses.filter((_, i) => i !== idx)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) {
      // Show all errors as toasts
      Object.values(errors).forEach((msg) => {
        if (Array.isArray(msg)) {
          msg.forEach((m) => m && toast.error(m));
        } else if (msg) {
          toast.error(msg);
        }
      });
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/cma-add-warehouses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cha_cma_id: cmaId, warehouses: form.warehouses }),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Warehouses added to CMA successfully!');
        navigate('/cma');
      } else {
        toast.error(data.message || 'Error occurred.');
      }
    } catch (err) {
      toast.error('Network error.');
    }
    setLoading(false);
  };

  if (!cmaData) {
    return (
      <div className="max-w-8xl mx-auto p-4 bg-white">
        <div className="text-center text-blue-600 py-6 text-sm">Loading CMA data...</div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <ToastContainer />
      <div className="flex justify-between items-center border-b pb-3 mb-5">
        <h2 className="text-lg font-medium text-gray-800">Add Warehouses to CMA - {cmaData.cma_name}</h2>
        <button
          type="button"
          onClick={() => navigate('/cma')}
          className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-3 py-1 text-xs rounded  font-normal hover:bg-[#10a5e9] hover:text-white hover:border-[#10a5e9] transition"
        >
          <FaArrowLeft />
          Back to List
        </button>
      </div>

     

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Warehouses Table */}
        <div className="pt-2">
          <label className="block text-sm font-medium text-gray-800 mb-2">Add New Warehouses</label>
          <div className="overflow-x-auto rounded-none border border-gray-300">
            <table className="min-w-full bg-white text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-1.5 border-b text-left font-medium">Name</th>
                  <th className="px-3 py-1.5 border-b text-left font-medium">Type</th>
                  <th className="px-3 py-1.5 border-b text-left font-medium">Location</th>
                  <th className="px-3 py-1.5 border-b text-left font-medium">State</th>
                  <th className="px-3 py-1.5 border-b text-left font-medium">State Code</th>
                  <th className="px-3 py-1.5 border-b"></th>
                </tr>
              </thead>
              <tbody>
                {form.warehouses.map((warehouse, idx) => (
                  <tr key={idx} className="border-b last:border-b-0">
                    <td className="px-3 py-1.5">
                      <input type="text" name="warehouse_name" placeholder="Warehouse Name" value={warehouse.warehouse_name} onChange={e => handleWarehouseChange(idx, e)} onBlur={e => handleWarehouseBlur(idx, e)} className="w-full border border-gray-300 px-2 py-1 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required autoComplete="off" maxLength={120} />
                    </td>
                    <td className="px-3 py-1.5">
                      <select name="type" value={warehouse.type} onChange={e => handleWarehouseChange(idx, e)} onBlur={e => handleWarehouseBlur(idx, e)} className="w-full border border-gray-300 px-2 py-1 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required>
                        <option value="">Type</option>
                        <option value="PORT">PORT</option>
                        <option value="DOMESTIC">DOMESTIC</option>
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <input type="text" name="location" placeholder="Location" value={warehouse.location} onChange={e => handleWarehouseChange(idx, e)} onBlur={e => handleWarehouseBlur(idx, e)} className="w-full border border-gray-300 px-2 py-1 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required autoComplete="off" maxLength={120} />
                    </td>
                    <td className="px-3 py-1.5">
                      <select name="state" value={warehouse.state} onChange={e => handleWarehouseChange(idx, e)} onBlur={e => handleWarehouseBlur(idx, e)} className="w-full border border-gray-300 px-2 py-1 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required>
                        <option value="">Select State</option>
                        {stateList.map((s) => (
                          <option key={s.code} value={s.name}>{s.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <input type="text" name="state_code" value={warehouse.state_code} readOnly className="w-full border border-gray-300 px-2 py-1 text-sm bg-gray-100 focus:outline-none focus:border-[#10a5e9] rounded-none" maxLength={10} />
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      {form.warehouses.length > 1 && (
                        <button type="button" onClick={() => handleRemoveWarehouse(idx)} className="text-sm px-3 py-1 border rounded text-[#10a5e9] border-[#10a5e9] hover:bg-[#10a5e9] hover:text-white hover:border-[#10a5e9]">Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-2">
            <button type="button" onClick={handleAddWarehouse} className="text-sm px-3 py-1 rounded border font-medium bg-[#10a5e9] text-white border-[#10a5e9] hover:bg-[#0e8ac2]">+ Add Warehouse</button>
          </div>
        </div>
        <div className="pt-2 flex items-center gap-3">
          <button type="submit" className="px-4 py-2 text-sm bg-[#10a5e9] text-white font-normal rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60" style={{ minWidth: '120px' }} disabled={loading}>
            {loading ? 'Saving...' : 'Add Warehouses'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CmaAddWarehousesForm; 